import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Clock, MessageCircle, ArrowLeft, Upload, FileText, Send, User, Award, X, Download, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { getStoredPartner, partnerHome } from '../lib/partnerContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending_payment: { label: 'En attente de paiement', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  paid: { label: 'Payé - En attente du RDV', color: 'bg-blue-100 text-blue-800', icon: Clock },
  scheduled: { label: 'RDV programmé', color: 'bg-purple-100 text-purple-800', icon: Clock },
  completed: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
};

export default function ConseilSuiviPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partnerHomePath = partnerHome(getStoredPartner());
  const conseilId = searchParams.get('id');
  const email = searchParams.get('email');
  
  const [conseil, setConseil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);

  const fetchConseil = async () => {
    if (!conseilId || !email) {
      setError("Lien invalide");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/api/conseil/suivi/${conseilId}?email=${encodeURIComponent(email)}`);
      setConseil(response.data);
    } catch (err) {
      console.error("Error fetching conseil:", err);
      setError(err.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConseil();
    // Poll for updates every 30s
    const interval = setInterval(fetchConseil, 30000);
    return () => clearInterval(interval);
  }, [conseilId, email]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/api/conseil/${conseilId}/client-message?email=${encodeURIComponent(email)}&content=${encodeURIComponent(newMessage)}`);
      setNewMessage('');
      await fetchConseil();
      toast.success('Message envoyé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi message');
    } finally {
      setSendingMessage(false);
    }
  };

  const uploadFiles = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingFiles(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => formData.append('files', file));
      
      await axios.post(`${API}/api/conseil/${conseilId}/client-upload?email=${encodeURIComponent(email)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      await fetchConseil();
      toast.success('Documents ajoutés');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploadingFiles(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <X className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-navy mb-2">Accès refusé</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate(partnerHomePath)}>Retour à l'accueil</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[conseil?.status] || STATUS_CONFIG.pending_payment;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-navy text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(partnerHomePath)}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Accueil
              </Button>
              <div className="h-5 w-px bg-white/20" />
              <h1 className="font-heading text-lg font-bold">Suivi Conseil Stratégique</h1>
            </div>
            <Badge className={statusConfig.color}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Info Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-navy/10 flex items-center justify-center">
                <Award className="w-6 h-6 text-navy" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-lg font-bold text-navy">
                  {conseil?.conseil_type_label || 'Conseil Stratégique'}
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Demandé le {new Date(conseil?.created_at).toLocaleDateString('fr-FR')}
                </p>
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-navy/80">{conseil?.description}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documents Section */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-navy flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Documents ({conseil?.files?.length || 0})
              </h3>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  onChange={uploadFiles}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFiles}
                >
                  {uploadingFiles ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Ajouter des documents
                </Button>
              </div>
            </div>
            
            {conseil?.files && conseil.files.length > 0 ? (
              <div className="space-y-2">
                {conseil.files.map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-navy/60" />
                      <div>
                        <p className="text-sm font-medium text-navy">{file.original_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024).toFixed(0)} Ko • {new Date(file.uploaded_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(`${API}/api/conseil/${conseilId}/files/${file.id}`, '_blank')}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>Aucun document pour le moment</p>
                <p className="text-xs mt-1">Ajoutez vos devis, photos ou plans ici</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Messages Section */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-heading font-bold text-navy flex items-center gap-2 mb-4">
              <MessageCircle className="w-5 h-5" />
              Échanges
            </h3>
            
            {/* Messages List */}
            <div className="space-y-4 max-h-[400px] overflow-y-auto mb-4">
              {conseil?.messages && conseil.messages.length > 0 ? (
                conseil.messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender === 'admin' ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className={`max-w-[80%] ${msg.sender === 'admin' ? 'order-2' : 'order-1'}`}>
                      <div
                        className={`p-3 rounded-xl ${
                          msg.sender === 'admin'
                            ? 'bg-navy text-white rounded-tl-none'
                            : 'bg-gray-100 text-navy rounded-tr-none'
                        }`}
                      >
                        <p className="text-sm">{msg.content}</p>
                      </div>
                      <div className={`flex items-center gap-2 mt-1 text-xs text-muted-foreground ${msg.sender === 'admin' ? '' : 'justify-end'}`}>
                        {msg.sender === 'admin' && <Award className="w-3 h-3 text-gold" />}
                        <span>{msg.sender_name}</span>
                        <span>•</span>
                        <span>{new Date(msg.created_at).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>Aucun message pour le moment</p>
                  <p className="text-xs mt-1">L'expert vous contactera bientôt</p>
                </div>
              )}
            </div>
            
            {/* New Message Input */}
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Écrivez votre message..."
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                className="flex-1"
              />
              <Button
                onClick={sendMessage}
                disabled={sendingMessage || !newMessage.trim()}
                className="bg-navy hover:bg-navy/90"
              >
                {sendingMessage ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
